import { useCallback, useLayoutEffect, useState, useMemo } from 'react';
import { keyBy } from 'lodash';
import moment from 'moment';
import { InputTypes } from 'taboola-ultimate-ui';
import { STATIC_FILTERS_MAP } from 'modules/campaigns/modules/campaigns-reports/config';
import {
    FILTER_OPERATOR,
    RANGE_FILTERS_OPERATORS,
} from 'modules/campaigns/modules/campaigns-reports/config/filters/filterOperator';
import { METAMODEL_FILTER_DATA_TYPE } from 'modules/campaigns/modules/campaigns-reports/services/dynamicReportsService';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { comparableFiltersPlaceholdersResolver } from './comparableFiltersPlaceholdersResolver';

const EMPTY_VALUES = [];
const PLAIN_VALUE_OPERATORS = keyBy([
    RANGE_FILTERS_OPERATORS.BETWEEN,
    RANGE_FILTERS_OPERATORS.LESS,
    RANGE_FILTERS_OPERATORS.GREATER,
    FILTER_OPERATOR.EQUALS,
    FILTER_OPERATOR.NOT_EQUALS,
    FILTER_OPERATOR.LESS_THAN,
    FILTER_OPERATOR.GREATER_THAN,
    FILTER_OPERATOR.LIKE,
    FILTER_OPERATOR.BETWEEN,
    FILTER_OPERATOR.AFTER,
    FILTER_OPERATOR.BEFORE,
]);
const showInputCondition = (operator, filterConfig) =>
    !filterConfig?.options && !!operator && !!PLAIN_VALUE_OPERATORS[operator];
const compareValues = (value, additionalValue, valueType) => {
    return valueType === InputTypes.DATE
        ? moment(value).isSameOrBefore(moment(additionalValue))
        : Number(value) < Number(additionalValue);
};

export const useFilterPlainValuesState = ({ initialFilter, operator, type, availableFiltersMap }) => {
    const filterConfig = availableFiltersMap[type];
    const valueType = useMemo(() => {
        if (STATIC_FILTERS_MAP[type]?.isDate) {
            return InputTypes.DATE;
        }
        if (!filterConfig?.dataType || filterConfig?.dataType === METAMODEL_FILTER_DATA_TYPE.NUMERIC) {
            return InputTypes.NUMBER;
        }
        return InputTypes.STRING;
    }, [type, filterConfig?.dataType]);
    const { values: initialValues = EMPTY_VALUES } = initialFilter || {};
    const [value, setValue] = useState(initialValues[0] ?? '');
    const [additionalValue, setAdditionalValue] = useState(initialValues[1] ?? '');
    const showInput = showInputCondition(operator, filterConfig);
    const showAdditionalInput =
        (operator === RANGE_FILTERS_OPERATORS.BETWEEN || operator === FILTER_OPERATOR.BETWEEN) && showInput;
    const shouldShowRangePicker = operator === FILTER_OPERATOR.BETWEEN && STATIC_FILTERS_MAP[type]?.isDate;
    const { currency } = useAccount();
    const { formatMessage } = useIntl();
    const isFilterWithCurrency = STATIC_FILTERS_MAP[type]?.isCurrency;
    const isFilterWithPercent = STATIC_FILTERS_MAP[type]?.isPercent;
    const { placeholder, additionalPlaceholder } = comparableFiltersPlaceholdersResolver({
        isFilterWithCurrency,
        isFilterWithPercent,
        showAdditionalInput,
        currency,
        formatMessage,
    });
    const cleanValues = useCallback(() => {
        setValue('');
        setAdditionalValue('');
    }, []);
    const isCompleteCondition = () =>
        !showInput ||
        (value !== '' &&
            (!showAdditionalInput || (additionalValue !== '' && compareValues(value, additionalValue, valueType))));

    useLayoutEffect(() => {
        if (!showInput) {
            setValue('');
        }
        if (!showAdditionalInput) {
            setAdditionalValue('');
        }
    }, [showAdditionalInput, showInput]);

    return {
        showInput,
        valueType,
        isComplete: isCompleteCondition(),
        value,
        setValue,
        placeholder,
        showAdditionalInput,
        additionalValue,
        setAdditionalValue,
        additionalPlaceholder,
        cleanValues,
        shouldShowRangePicker,
    };
};
